import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export function ContactFooter() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.sendMessage(form);
      toast.success("Thanks for reaching out — we'll get back to you soon!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <section id="contact" className="bg-[#FAF5EE]" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#D97706] font-semibold" data-testid="contact-overline">Say Hello</p>
            <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2C221E] tracking-tight" data-testid="contact-heading">
              Questions, custom orders, or just bee-curious?
            </h2>
            <p className="mt-5 text-base text-[#5A4D44] leading-relaxed" data-testid="contact-text">
              We'd love to hear from you — whether it's a question about our candles, a custom batch for a
              gift or event, or you just want to talk bees. Drop us a note and the family will get back to you.
            </p>
            <div className="mt-8 bg-[#F3EBE0] rounded-2xl p-6 border border-[#E6D7C3]">
              <p className="font-serif-display font-semibold text-[#2C221E]">Good to know</p>
              <ul className="mt-3 space-y-2 text-sm text-[#5A4D44]">
                <li>· Every product is handmade in small family batches</li>
                <li>· Raw honey arrives with the Spring 2027 harvest</li>
                <li>· Custom and gift orders welcome</li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6D7C3] p-8 flex flex-col gap-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                  className="w-full px-5 py-3.5 rounded-xl border border-[#E6D7C3] text-sm text-[#2C221E] placeholder-[#8C7A6B] focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Your email"
                  data-testid="contact-email-input"
                  className="w-full px-5 py-3.5 rounded-xl border border-[#E6D7C3] text-sm text-[#2C221E] placeholder-[#8C7A6B] focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                />
              </div>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="What can we help with?"
                data-testid="contact-message-input"
                className="w-full px-5 py-3.5 rounded-xl border border-[#E6D7C3] text-sm text-[#2C221E] placeholder-[#8C7A6B] focus:outline-none focus:ring-2 focus:ring-[#D97706] resize-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="self-start px-8 py-3.5 rounded-full bg-[#2C221E] hover:bg-[#D97706] text-[#FAF5EE] font-semibold text-sm transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="bg-[#2C221E]" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo size={34} />
            <div className="leading-tight">
              <span className="font-serif-display font-bold text-[#FAF5EE] block">McGee's Bees</span>
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#D97706]">A little sweet. A little wild.</span>
            </div>
          </div>
          <p className="text-xs text-[#8C7A6B] text-center" data-testid="footer-copy">
            © {new Date().getFullYear()} McGee's Bees · A family apiary · Honey arriving Spring 2027
          </p>
          <a href="/admin/login" className="text-xs text-[#8C7A6B] hover:text-[#D97706] transition-colors" data-testid="footer-admin-link">
            Family Login
          </a>
        </div>
      </footer>
    </>
  );
}
